import { Button, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material'
import React, { ReactNode, useState } from 'react'
import { SaveButton, SimpleForm, Toolbar, useCreate, useNotify, useRefresh, useTranslate, } from 'react-admin'

interface FormValues {
    formValues: {
        [key: string]: unknown
    }
}

interface CreateDialogProps {
  resource: string
  defaultValues?: object
  renderTrigger: (open: () => void) => ReactNode
  dialogTitle: string
  dialogSubtitle?: string
  dialogIcon?: ReactNode
  children: ReactNode
}

const CreateDialog: React.FC<CreateDialogProps> = ({
  resource,
  defaultValues = {},
  renderTrigger,
  dialogTitle,
  dialogSubtitle,
  dialogIcon,
  children,
}) => {
  const [open, setOpen] = useState(false)
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const [create, { error, isLoading }] = useCreate()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const save = (values: unknown) => {
    const formValues = values as FormValues
    void create(
      resource,
      { data: { ...defaultValues, ...formValues.formValues } },
      {
        onSuccess: () => {
          notify('ra.notification.created', { type: 'info' })
          handleClose()
          refresh()
        },
        onError: (createError: unknown) => {
          const apiError = createError as {
            message?: string
            body?: { detail?: string | object[]; message?: string }
          }
          const message =
            apiError?.body?.message ||
            apiError?.message ||
            (typeof apiError?.body?.detail === 'string' ? apiError?.body?.detail : null) ||
            'ra.notification.http_error'

          notify(message, { type: 'warning' })
        },
      }
    )
  }

  return (
    <>
      { renderTrigger(handleOpen) }
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ ml: 2 }}>
          {dialogIcon}
          <span style={{ marginRight: '8px' }} />
          <span style={{ paddingTop: '-10px' }}>{translate(dialogTitle)}</span>
        </DialogTitle>
        <DialogContent>
          {dialogSubtitle && (
            <Typography variant="body2" sx={{ ml: 2 }} color="textSecondary">
              {translate(dialogSubtitle)}
            </Typography>
          )}
          <SimpleForm
            onSubmit={save}
            toolbar={
              <Toolbar>
                <SaveButton disabled={isLoading} />
                <Button onClick={handleClose}>{translate('ra.action.cancel')}</Button>
              </Toolbar>
            }
            defaultValues={defaultValues}
          >
            {children}
          </SimpleForm>
          {!!error && (
            <p style={{ color: 'red' }}>
              {translate('ra.notification.http_error')}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CreateDialog
